"use client";
import { useMemo } from "react";

import clsx from "clsx";

import { Column } from "@/components/containers";
import { FreeShippingCampaignTag } from "@/components/domains/campaign";
import { UKomiStar } from "@/components/domains/ukomi";
import { FEATURE_FLAG } from "@/configs/system";
import { isActiveProductCaution } from "@/features/product-caution";
import { useListProductCautions } from "@/generated/open-api/product-caution/product-caution";
import { ProductModel } from "@/models/product/type";
import { formatCurrency } from "@/utils/currency";

import styles from "./ProductInfo.module.scss";

export interface ProductInfoProps {
  product: ProductModel;
  title?: string;
  description?: string;
  note?: string;
  noteCaution?: string;
  isNew?: boolean;
  isLimited?: boolean;
}

/**
 * TODO: productはコンポーネントの中で取るようにする
 */
export function ProductInfo({
  title,
  product,
  description,
  note,
  noteCaution,
  isNew,
  isLimited,
}: ProductInfoProps) {
  const taxText = product.isSetProduct ? "（送料・税込）" : "（税込）";

  /**
   * 通常商品:定期購入の安い価格を表示する(firstSubscriptionPrice)
   * セット商品は:この画面からは定期購入のみとなるため、定期購入価格を表示する(firstSubscriptionPrice)
   * アウトレット商品は:定期購入できないため通常価格を表示する(price)
   */
  const price = product.isOutlet ? product.price : product.firstSubscriptionPrice;
  const isNormalProduct = !product.isOutlet && !product.isSetProduct;

  const { data: productCautionsData } = useListProductCautions({});

  const activeCautions = useMemo(() => {
    const productCautions = productCautionsData?.data ?? [];
    if (product.isSetProduct) {
      return productCautions.filter((caution) =>
        product.setProducts?.some(({ variantId }) =>
          isActiveProductCaution(caution, {
            area: "PRODUCT_DETAIL",
            variantId,
            temperature: product.isFreeze ? "FROZEN" : "NORMAL",
          })
        )
      );
    } else {
      return productCautions.filter((caution) =>
        isActiveProductCaution(caution, {
          area: "PRODUCT_DETAIL",
          variantId: product.variantId,
          temperature: product.isFreeze ? "FROZEN" : "NORMAL",
        })
      );
    }
  }, [product, productCautionsData]);

  return (
    <>
      <div className="mg__bottom__m mg__top__s mg__top__off__pc">
        {product.isFreeze && (
          <div className="mg__bottom__xs">
            <span className="tag bg__darkblue inline square mini text__white">冷凍</span>
          </div>
        )}
        {FEATURE_FLAG.isFreeShippingCampaignActive && !product.isFreeze && product.isSetProduct && (
          <FreeShippingCampaignTag />
        )}
        {isLimited && (
          <div className="mg__bottom__xs">
            <span className="tag bg__red inline square mini text__white">期間限定</span>
          </div>
        )}
        <p className="text__m text__bold">{product.title}</p>
        <p className="text__xl text__bold">
          <span>{product.variantTitle} </span>
          {isNew && <span className="text__red">NEW</span>}
        </p>
        {!product.isOutlet && (
          <UKomiStar
            groupName={product.ukomiGroupName ?? product.variantTitle}
            name={product.name}
            price={product.price}
          />
        )}
      </div>
      <div>
        <p className="text__s">
          価格：
          <span className="text__l text__bold">
            {formatCurrency(price)}
            {isNormalProduct && "〜"}
          </span>
          <span className="text__m">
            /{product.meal} {taxText}
            {/* セット商品は、コースの選択のみになるため、価格の注釈が不要 */}
            {isNormalProduct && <sup className="sup">※1</sup>}
          </span>
        </p>
        <Column className="mg__top__s">
          {title && <p className="text__m text__bold">{title}</p>}
          {description && (
            <p className={clsx("text__m mg__top__s", styles.description)}>{description}</p>
          )}
          {note && <p className="text__s text__red mg__top__s">{note}</p>}
          {activeCautions.map((deliveryDelayMessage) => (
            <p key={deliveryDelayMessage.id} className="text__s text__red mg__top__s">
              {deliveryDelayMessage.message}
            </p>
          ))}

          {noteCaution && (
            <p className={clsx("text__s text__gray__dark mg__top__s", styles.description)}>
              {noteCaution}
            </p>
          )}
        </Column>
      </div>
    </>
  );
}
